import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  FacilityEvent,
  FacilityEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/core/v1/facility_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { FacilityCreatedDetail } from './FacilityCreatedDetail';
import { FacilityDeletedDetail } from './FacilityDeletedDetail';
import { FacilityGroupAssignedDetail } from './FacilityGroupAssignedDetail';
import { FacilityGroupRemovedDetail } from './FacilityGroupRemovedDetail';
import { FacilityUpdatedDetail } from './FacilityUpdatedDetail';

import { useTranslations } from '@/facilities/i18n/useTranslations';

type FacilityEventDetailProps = {
  payload: string;
};

export const FacilityEventDetail = ({ payload }: FacilityEventDetailProps) => {
  const t = useTranslations();
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): FacilityEvent | null => {
    try {
      return fromJson(FacilityEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title={t.logs.rawPayloadParseError} />;
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <FacilityCreatedDetail event={inner.value} />;
      case 'updated':
        return <FacilityUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <FacilityDeletedDetail event={inner.value} />;
      case 'groupAssigned':
        return <FacilityGroupAssignedDetail event={inner.value} />;
      case 'groupRemoved':
        return <FacilityGroupRemovedDetail event={inner.value} />;
      case undefined:
        return <JsonViewer data={payload} title={t.logs.unknownEventType} />;
    }
  };

  const getEventTitle = () => {
    switch (inner.case) {
      case 'created':
        return t.logs.events.facilityCreated;
      case 'updated':
        return t.logs.events.facilityUpdated;
      case 'deleted':
        return t.logs.events.facilityDeleted;
      case 'groupAssigned':
        return t.logs.events.facilityGroupAssigned;
      case 'groupRemoved':
        return t.logs.events.facilityGroupRemoved;
      case undefined:
        return t.logs.unknownEvent;
    }
  };

  return (
    <Col className="@FacilityEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
